import SwapService from '@/services/SwapService'
import AdService from '@/services/AdService'
import * as Sentry from '@sentry/browser'
import posthog from 'posthog-js'
import { track } from '@/utils/track'

function cacheKey (bucket, sizes) {
  const sortedSizes = (sizes || []).slice().sort().join(',')
  return bucket + ':' + sortedSizes
}

// Flatten a page of suggestions into scalar props. PostHog filters and funnels
// on flat values, so chain data is summarised here rather than sent as objects.
function summariseSuggestions (results) {
  const list = results || []
  const sizes = {}
  let withLiveChains = 0
  let liveChainsTotal = 0
  let bestProgressPct = 0

  list.forEach(item => {
    const stats = item.chain_stats || {}
    const live = stats.total_live_chains || 0
    if (live > 0) withLiveChains += 1
    liveChainsTotal += live
    if ((stats.best_progress_pct || 0) > bestProgressPct) {
      bestProgressPct = stats.best_progress_pct
    }
    ;(item.swap_sizes || []).forEach(size => {
      sizes[size] = (sizes[size] || 0) + 1
    })
  })

  return {
    result_count: list.length,
    with_live_chains: withLiveChains,
    live_chains_total: liveChainsTotal,
    best_progress_pct: bestProgressPct,
    direct_swaps: sizes[2] || 0,
    three_way_swaps: sizes[3] || 0,
    four_way_swaps: sizes[4] || 0
  }
}

function initialState () {
  return {
    suggestions: [],
    suggestionsLoading: false,
    suggestionsRefreshing: false,
    suggestionsCache: {},
    suggestionsFetchSeq: 0,
    fromAdId: null,
    bucketTotals: {},
    pagination: null,
    activeBucket: 'suggestion',
    activeSwapSizes: [2, 3],
    detail: null,
    detailLoading: false,
    detailFetchSeq: 0,
    v2Matches: [],
    v2MatchesLoading: false,
    v2MatchesFetchSeq: 0,
    markingInterest: {}
  }
}

export default {
  namespaced: true,
  state: initialState(),
  mutations: {
    SET_SUGGESTIONS (state, list) {
      state.suggestions = list
    },
    APPEND_SUGGESTIONS (state, list) {
      state.suggestions = state.suggestions.concat(list)
    },
    UPDATE_SUGGESTION_INTEREST (state, { toAdId, interest }) {
      state.suggestions = state.suggestions.map(s =>
        s.to_ad_id === toAdId ? { ...s, _userInterest: interest } : s
      )
    },
    SET_BUCKET_TOTALS (state, totals) {
      state.bucketTotals = totals || {}
    },
    SET_PAGINATION (state, pagination) {
      state.pagination = pagination || null
    },
    SET_FROM_AD_ID (state, id) {
      state.fromAdId = id || null
    },
    SET_SUGGESTIONS_LOADING (state, loading) {
      state.suggestionsLoading = loading
    },
    SET_SUGGESTIONS_REFRESHING (state, refreshing) {
      state.suggestionsRefreshing = refreshing
    },
    SET_ACTIVE_BUCKET (state, bucket) {
      state.activeBucket = bucket
    },
    SET_ACTIVE_SWAP_SIZES (state, sizes) {
      state.activeSwapSizes = sizes
    },
    SET_CACHE_ENTRY (state, { key, payload }) {
      state.suggestionsCache = {
        ...state.suggestionsCache,
        [key]: payload
      }
    },
    RESET_CACHE (state) {
      state.suggestionsCache = {}
    },
    INC_SUGGESTIONS_SEQ (state) {
      state.suggestionsFetchSeq += 1
    },
    INC_DETAIL_SEQ (state) {
      state.detailFetchSeq += 1
    },
    INC_V2_MATCHES_SEQ (state) {
      state.v2MatchesFetchSeq += 1
    },
    SET_DETAIL (state, detail) {
      state.detail = detail
    },
    SET_DETAIL_LOADING (state, loading) {
      state.detailLoading = loading
    },
    SET_V2_MATCHES (state, list) {
      state.v2Matches = list
    },
    SET_V2_MATCHES_LOADING (state, loading) {
      state.v2MatchesLoading = loading
    },
    SET_MARKING_INTEREST (state, { toAdId, marking }) {
      state.markingInterest = {
        ...state.markingInterest,
        [toAdId]: marking
      }
    },
    RESET_ALL (state) {
      const fresh = initialState()
      Object.keys(fresh).forEach(key => {
        state[key] = fresh[key]
      })
    }
  },
  actions: {
    fetchSuggestions ({ state, commit }, { page = 1, append = false } = {}) {
      commit('INC_SUGGESTIONS_SEQ')
      const mySeq = state.suggestionsFetchSeq

      const key = cacheKey(state.activeBucket, state.activeSwapSizes)
      const cached = state.suggestionsCache[key]

      if (!append && page === 1) {
        if (cached) {
          // Only restore per-bucket data (results + pagination).
          // bucket_totals and from_ad_id are global and always reflect the
          // freshest response we've seen — never roll them back from cache.
          commit('SET_SUGGESTIONS', cached.results || [])
          commit('SET_PAGINATION', cached.pagination)
          commit('SET_SUGGESTIONS_REFRESHING', true)
        } else {
          commit('SET_SUGGESTIONS', [])
          commit('SET_SUGGESTIONS_LOADING', true)
        }
      } else {
        commit('SET_SUGGESTIONS_LOADING', true)
      }

      const params = {
        page,
        buckets: state.activeBucket,
        swap_sizes: state.activeSwapSizes.join(',')
      }
      return new Promise((resolve, reject) => {
        SwapService.getSwaps(params)
          .then(response => {
            if (mySeq !== state.suggestionsFetchSeq) {
              // A newer request has superseded this one — drop the result.
              resolve(null)
              return
            }
            const payload = response.data.data
            if (append) {
              commit('APPEND_SUGGESTIONS', payload.results || [])
            } else {
              commit('SET_SUGGESTIONS', payload.results || [])
            }
            commit('SET_BUCKET_TOTALS', payload.bucket_totals)
            commit('SET_PAGINATION', payload.pagination)
            commit('SET_FROM_AD_ID', payload.from_ad_id)
            if (!append && page === 1) {
              // Cache only what's specific to this (bucket, sizes) slice.
              commit('SET_CACHE_ENTRY', {
                key,
                payload: {
                  results: payload.results || [],
                  pagination: payload.pagination || null
                }
              })

              // Fires on a fresh first page only (not on append or cache
              // restore), so one event == one list a user was actually shown.
              // result_count: 0 is the important case: it records being
              // offered nothing, which no server-side table captures.
              const totals = payload.bucket_totals || {}
              const shown = summariseSuggestions(payload.results)
              shown.bucket = state.activeBucket
              shown.swap_sizes = state.activeSwapSizes.join(',')
              shown.total_available = totals[state.activeBucket] || 0
              track('swap_suggestions_shown', shown)
            }
            commit('SET_SUGGESTIONS_LOADING', false)
            commit('SET_SUGGESTIONS_REFRESHING', false)
            resolve(payload)
          })
          .catch(error => {
            if (mySeq !== state.suggestionsFetchSeq) {
              resolve(null)
              return
            }
            commit('SET_SUGGESTIONS_LOADING', false)
            commit('SET_SUGGESTIONS_REFRESHING', false)
            Sentry.captureException(error)
            posthog.captureException(error)
            reject(error)
          })
      })
    },
    fetchSwapDetail ({ state, commit }, toAdId) {
      commit('INC_DETAIL_SEQ')
      const mySeq = state.detailFetchSeq
      commit('SET_DETAIL_LOADING', true)
      commit('SET_DETAIL', null)
      return new Promise((resolve, reject) => {
        SwapService.getSwap(toAdId)
          .then(response => {
            if (mySeq !== state.detailFetchSeq) {
              resolve(null)
              return
            }
            const detail = response.data.data || {}
            const detailStats = detail.chain_stats || {}
            commit('SET_DETAIL', response.data.data)
            commit('SET_DETAIL_LOADING', false)
            track('swap_suggestion_opened', {
              to_ad_id: toAdId,
              swap_sizes: (detail.swap_sizes || []).join(','),
              live_chains_total: detailStats.total_live_chains || 0,
              best_progress_pct: detailStats.best_progress_pct || 0
            })
            resolve(response.data.data)
          })
          .catch(error => {
            if (mySeq !== state.detailFetchSeq) {
              resolve(null)
              return
            }
            commit('SET_DETAIL_LOADING', false)
            Sentry.captureException(error)
            posthog.captureException(error)
            reject(error)
          })
      })
    },
    fetchV2Matches ({ state, commit }) {
      commit('INC_V2_MATCHES_SEQ')
      const mySeq = state.v2MatchesFetchSeq
      commit('SET_V2_MATCHES_LOADING', true)
      // Clear previous results so a stale flash doesn't show between users.
      commit('SET_V2_MATCHES', [])
      return new Promise((resolve, reject) => {
        SwapService.getMatches()
          .then(response => {
            if (mySeq !== state.v2MatchesFetchSeq) {
              resolve(null)
              return
            }
            const matches = response.data.data || []
            commit('SET_V2_MATCHES', matches)
            commit('SET_V2_MATCHES_LOADING', false)
            // The headline number: how many real matches a member has, zero
            // included. Everything else is a leading indicator of this.
            track('swap_matches_shown', { match_count: matches.length })
            resolve(response.data.data)
          })
          .catch(error => {
            if (mySeq !== state.v2MatchesFetchSeq) {
              resolve(null)
              return
            }
            commit('SET_V2_MATCHES_LOADING', false)
            Sentry.captureException(error)
            posthog.captureException(error)
            reject(error)
          })
      })
    },
    setBucket ({ commit, dispatch }, bucket) {
      commit('SET_ACTIVE_BUCKET', bucket)
      return dispatch('fetchSuggestions', { page: 1, append: false })
    },
    setSwapSizes ({ commit, dispatch }, sizes) {
      commit('SET_ACTIVE_SWAP_SIZES', sizes)
      return dispatch('fetchSuggestions', { page: 1, append: false })
    },
    markInterest ({ state, commit }, { toAdId, interested }) {
      const fromAdId = state.fromAdId
      if (!fromAdId || !toAdId) return Promise.resolve()

      const newInterest = interested ? 1 : 0
      commit('SET_MARKING_INTEREST', { toAdId, marking: true })
      // Optimistically flip the card's displayed state — feels snappy.
      commit('UPDATE_SUGGESTION_INTEREST', { toAdId, interest: newInterest })

      const call = interested
        ? AdService.likeAd(fromAdId, toAdId, 1)
        : AdService.passAd(fromAdId, toAdId, 0)

      return new Promise((resolve, reject) => {
        call
          .then(response => {
            commit('SET_MARKING_INTEREST', { toAdId, marking: false })
            // The `likes` table records the verdict but not the context it was
            // made in. Capture which bucket and chain sizes produced it.
            track('swap_interest_marked', {
              to_ad_id: toAdId,
              interested: !!interested,
              bucket: state.activeBucket,
              swap_sizes: state.activeSwapSizes.join(',')
            })
            // Bucket totals and memberships in other buckets changed — drop
            // cached pages so the next switch refetches accurate counts.
            commit('RESET_CACHE')
            resolve(response)
          })
          .catch(error => {
            commit('SET_MARKING_INTEREST', { toAdId, marking: false })
            // Roll back the optimistic flip so the UI reflects server truth.
            commit('UPDATE_SUGGESTION_INTEREST', { toAdId, interest: undefined })
            Sentry.captureException(error)
            posthog.captureException(error)
            reject(error)
          })
      })
    },
    invalidateCache ({ commit }) {
      commit('RESET_CACHE')
    },
    resetAll ({ commit }) {
      commit('RESET_ALL')
    }
  },
  getters: {
    hasMore: state =>
      !!(state.pagination && state.pagination.page < state.pagination.total_pages),
    nextPage: state =>
      state.pagination ? state.pagination.page + 1 : 1,
    isMarkingInterest: state => toAdId =>
      !!state.markingInterest[toAdId]
  }
}
